import React from 'react';
import ArchivingProfileStore from 'stores/ArchivingProfileStore';
import DepartmentFormStore from 'stores/department/DepartmentFormStore';
import AltContainer from 'alt-container';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import RepProfilePageStore from 'stores/RepProfilePageStore';
import UserActions from 'actions/UserActions';
import SkillActions from 'actions/SkillActions';
import SkillStore from 'stores/SkillStore';
import TeamActions from 'actions/TeamActions';
import TeamListStore from 'stores/TeamListStore';
import LibraryActions from 'actions/library/LibraryActions';
import FileUploadCallDetailsActions from 'actions/files/FileUploadCallDetailsActions';
import RepProfilePage from 'components/dashboard/rep/RepProfilePage';
import {CoachingDirection, RepProfileTab, skillsFetchParametersShared} from '@store/coaching-plans';
import connectToStores from 'alt-utils/lib/connectToStores';

import AuthStore from 'stores/AuthStore';
import FilePlayerWaveformStore from 'stores/files/FilePlayerWaveformStore';
import FileUploadCallCardStore from 'stores/files/FileUploadCallCardStore';
import FileUploadCallDetailsStore from 'stores/files/FileUploadCallDetailsStore';
import LibraryCreateFormStore from 'stores/library/LibraryCreateFormStore';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import LibraryStore from 'stores/library/LibraryStore';
import FileUploadStore from 'stores/files/FileUploadStore';
import UserStore from 'stores/UserStore';
import {List} from 'immutable';
import CoachingReviewTypeStore from 'stores/coaching/CoachingReviewTypeStore';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import DashboardDateRangeStore, {
  dateRangeForDashboard,
  dateRangeForHeader,
} from 'stores/dashboard/DashboardDateRangeStore';
import {shouldUpdate} from 'components/StatsPage2.utils';

export function mapTabIndexToPageName(tabIndex, direction) {
  switch (tabIndex) {
    case RepProfileTab.SUMMARY:
      return 'summary';
    case RepProfileTab.CALL_ACTIVITY:
      return 'call-activity';
    case RepProfileTab.REVIEW_ACTIVITIES:
      if (direction === CoachingDirection.review) {
        return 'self-review';
      } else if (direction === CoachingDirection.coach) {
        return 'other-review';
      }
      break;
    case RepProfileTab.COACHING_PLANS:
      return 'coaching-plans';
    case RepProfileTab.COACHING_RECEIVED:
      if (direction === CoachingDirection.manager) {
        return 'coaching-from-manager';
      } else if (direction === CoachingDirection.other) {
        return 'coaching-from-others';
      }
      break;
    case RepProfileTab.KPI:
      return 'kpi';
    case RepProfileTab.CALLS:
      return 'calls';
    default:
      return 'summary';
  }
}

export class RepCoachingPlansContainer extends React.PureComponent {
  render() {
    return <RepProfilePageContainer {...this.props} page={RepProfileTab.COACHING_PLANS} />;
  }
}

export class RepCallActivityContainer extends React.PureComponent {
  render() {
    return <RepProfilePageContainer {...this.props} page={RepProfileTab.CALL_ACTIVITY} />;
  }
}

export class RepSelfReviewContainer extends React.PureComponent {
  render() {
    return (
      <RepProfilePageContainer
        {...this.props}
        page={RepProfileTab.REVIEW_ACTIVITIES}
        reviewType={CoachingDirection.review}
      />
    );
  }
}

export class RepOtherReviewContainer extends React.PureComponent {
  render() {
    return (
      <RepProfilePageContainer
        {...this.props}
        page={RepProfileTab.REVIEW_ACTIVITIES}
        reviewType={CoachingDirection.coach}
      />
    );
  }
}

export class RepCoachingFromManagerContainer extends React.PureComponent {
  render() {
    return (
      <RepProfilePageContainer
        {...this.props}
        page={RepProfileTab.COACHING_RECEIVED}
        coachingReceivedFrom={CoachingDirection.manager}
      />
    );
  }
}

export class RepCoachingFromOthersContainer extends React.PureComponent {
  render() {
    return (
      <RepProfilePageContainer
        {...this.props}
        page={RepProfileTab.COACHING_RECEIVED}
        coachingReceivedFrom={CoachingDirection.other}
      />
    );
  }
}

export class RepCallsContainer extends React.PureComponent {
  render() {
    return <RepProfilePageContainer {...this.props} page={RepProfileTab.CALLS} />;
  }
}

/**
 * This class is mostly a clone of RepProfilePageContainer. It gathers data for the various coach/rep pages in dashv2.
 */
@connectToStores
class RepProfilePageContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.skillsFetchParameters = this.skillsFetchParameters.bind(this);
    this.fetchSkillTrends = this.fetchSkillTrends.bind(this);
    this.onSelectScoredByType = this.onSelectScoredByType.bind(this);
  }

  static getStores() {
    return [DashboardDateRangeStore];
  }

  static getPropsFromStores() {
    const dateRangeState = DashboardDateRangeStore.getState();
    const {startDate, endDate} = dateRangeForDashboard(dateRangeState);
    const {startDate: headerStartDate, endDate: headerEndDate} = dateRangeForHeader(dateRangeState);
    return {
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
    };
  }

  componentDidMount() {
    setTimeout(() => {
      CoachingReviewTypeActions.fetchAllReviewTypes();
      UserActions.fetchAll({ids: [AuthStore.getState().user.id]});
      LibraryActions.getShareAccessibleTeams();
      LibraryActions.getLibraries();
      LibraryActions.getTags();
      RepProfilePageActions.selectTabIndex(0);
      this.fetchParamDependentData();
    });
  }

  componentDidUpdate(prevProps) {
    if (shouldUpdate(prevProps, this.props, false, true)) {
      this.fetchParamDependentData(prevProps);
    }
  }

  // Front end really does not need to be using async here.  This is done so
  // backend is very sensitive to the request order and needs to be able to
  // use caches that it builds after the first request.  async/await causes
  // requests to run serially and not fire all at once
  async fetchParamDependentData(prevProps) {
    const {teamId, userId, tab, coachingReceivedFrom, reviewType} = this.getParams(this.props);
    const direction = coachingReceivedFrom || reviewType;

    tab && RepProfilePageActions.selectTabIndex(tab);

    const oldParams = prevProps ? this.getParams(prevProps) : {};
    if (teamId !== oldParams.teamId) {
      TeamActions.fetchUsers([teamId]);
    }
    const {startDate, endDate} = this.props;
    const {startDate: prevStartDate, endDate: prevEndDate} = prevProps ?? {};
    const dateRangeChanged =
      !prevStartDate ||
      !prevEndDate ||
      !startDate.isSame(prevProps.startDate, 'day') ||
      !endDate.isSame(prevProps.endDate, 'day');
    const repChanged = userId !== oldParams.userId;

    // entire page level data - if we changed date range / rep,
    // we need to reload these things
    if (dateRangeChanged || repChanged) {
      if (repChanged) {
        RepProfilePageActions.getCoachingPlans(userId);
      }

      this.fetchSkillTrends();
    }

    // depending on the tab we're on, now load the tab dependent data
    const users = await UserActions.fetchAll({ids: List([userId])});
    const user = users.get(0);
    const oldTab = oldParams.tab;
    this.getTabData(
      user,
      startDate,
      endDate,
      tab,
      direction,
      oldTab,
      dateRangeChanged || repChanged
    );
  }

  getTabData(user, inclStartDate, exclEndDate, tab, directionFromParams, oldTab, filtersChanged) {
    const {id: userId, teamId, managerId} = user;
    UserActions.fetchAll({teamId: teamId});

    if (!tab || tab === RepProfileTab.SUMMARY) {
      RepProfilePageActions.getSummary(userId, teamId, inclStartDate, exclEndDate);
      RepProfilePageActions.getCallMetrics({
        teamIds: [teamId],
        startDate: inclStartDate,
        endDate: exclEndDate,
        managerId,
      });
    }

    // if we just arrived (in the case of sub-tabs), load the default sub tab data
    const justLoadedTab = tab !== oldTab;

    if (tab === RepProfileTab.CALL_ACTIVITY) {
      RepProfilePageActions.getCallActivity(teamId, userId, inclStartDate, exclEndDate);
      RepProfilePageActions.getCallDonut(teamId, inclStartDate, exclEndDate);
      RepProfilePageActions.getCallTeamMetrics(
        teamId,
        userId,
        inclStartDate,
        exclEndDate,
        managerId
      );
    }

    if (tab === RepProfileTab.REVIEW_ACTIVITIES && (justLoadedTab || filtersChanged)) {
      const direction = directionFromParams ? directionFromParams : 'review';
      RepProfilePageActions.getCoachingActivity({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
      RepProfilePageActions.getCoachingTeamMetrics({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
    }

    if (tab === RepProfileTab.COACHING_PLANS) {
      // already have the data we need loaded for coaching skills / plans
    }

    if (tab === RepProfileTab.COACHING_RECEIVED && (justLoadedTab || filtersChanged)) {
      const direction = directionFromParams ? directionFromParams : 'manager';
      RepProfilePageActions.getCoachingActivity({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
      RepProfilePageActions.getCoachingTeamMetrics({
        direction,
        teamId,
        userId,
        startDate: inclStartDate,
        endDate: exclEndDate,
      });
    }

    if (tab === RepProfileTab.CALLS) {
      RepProfilePageActions.getTeamConversationTrend(teamId, inclStartDate, exclEndDate);
      RepProfilePageActions.getTeamCallDurationTrend(teamId, inclStartDate, exclEndDate);
    }
  }

  /**
   * Fetch skill trends. Used for skills tab
   *
   * @param startDate - override start if necessary
   * @param endDate - override end if necessary
   */
  fetchSkillTrends(startDate, endDate) {
    const {userId} = this.getParams(this.props);

    const fetchParams = this.skillsFetchParameters(
      userId,
      RepProfilePageStore.getState().selectedScoredByType
    );

    SkillActions.fetchUserTrend({
      ...fetchParams,
      from: fetchParams.from,
      to: fetchParams.to,
    }).then(teamTrends => {
      if (!teamTrends) return;

      // if they havent made a selection yet, change their scored by to default to ALL SCORES.
      // if they have selected a value, respect that intention.
      const {scoredByTypeWasSelected} = RepProfilePageStore.getState();
      if (!scoredByTypeWasSelected && teamTrends.length === 0) {
        this.onSelectScoredByType('ALL_SCORES');
      }
    });
  }

  skillsFetchParameters(userId, scoredBy) {
    const {startDate, endDate} = this.props;
    return skillsFetchParametersShared(userId, scoredBy, startDate, endDate);
  }

  onSelectScoredByType(scoredBy) {
    const params = this.getParams(this.props);
    const userId = params.userId;
    SkillActions.fetchUserTrend.defer({
      ...this.skillsFetchParameters(userId, scoredBy),
    });
    RepProfilePageActions.selectScoredByType.defer(scoredBy);
  }

  getParams(props) {
    const {teamId, coachId, repId, managerId, page: tab, coachingReceivedFrom, reviewType} = props;
    return {
      // XXX need teamId passed in if not authUser
      teamId: teamId || AuthStore.getState().user.teamId,
      userId: repId ? repId : coachId,
      managerId,
      tab: tab || RepProfileTab.SUMMARY,
      coachingReceivedFrom,
      reviewType,
    };
  }
  render() {
    const params = this.getParams(this.props);
    return (
      <AltContainer
        stores={{
          ArchivingProfileStore,
          AuthStore,
          DepartmentFormStore,
          FilePlayerWaveformStore,
          FileUploadCallCardStore,
          FileUploadCallDetailsStore,
          FileUploadStore,
          LibraryCreateFormStore,
          LibraryStore,
          ManagerDashboardStore,
          RepProfilePageStore,
          SkillStore,
          TeamListStore,
          UserStore,
          CoachingReviewTypeStore,
        }}
        actions={{
          FileUploadCallDetailsActions,
          LibraryActions,
          RepProfilePageActions,
          SkillActions,
        }}>
        <RepProfilePage
          {...this.props}
          startDate={this.props.startDate}
          endDate={this.props.endDate}
          headerStartDate={this.props.headerStartDate}
          headerEndDate={this.props.headerEndDate}
          onSelectScoredByType={this.onSelectScoredByType}
          repId={params.userId}
          getCallDetails={this.getCallDetails}
          getInfoAboutFiles={LibraryStore.getInfoAboutFiles}
        />
      </AltContainer>
    );
  }
}
